// core/offline/offline-db.ts
import Dexie, { Table } from 'dexie';

export type EntityRow = {
    pk?: number;
    collection: string;
    scopeKey: string;
    serverId?: string | null;
    clientId?: string | null;
    subsetKey?: string | null;
    pending?: boolean;
    updatedAt: number;
    dataEnc: string; // verschlüsselte JSON (oder plain, bis encryption aktiv)
};

export type OutboxRow = {
    id: string;                // correlation / idempotency key
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    url: string;               // gespeicherte URL (mit evtl. cid: in Pfad/Query)
    bodyEnc: string;           // verschlüsselte JSON des Bodys
    headersEnc: string;        // optionale non-sensitive Header (typ. leer)
    metaEnc: string;           // MutateMeta light (collection, scopeArgs, op, extractIdFromUrl yes/no)
    scopeKey: string;
    clientId?: string | null;  // primäre cid dieses Mutationsflusses (falls vorhanden)
    createdAt: number;
    replayCount: number;
};

export type IdMapRow = {
    cid: string;            // "cid:...."
    collection: string;
    scopeKey: string;
    sid: string;            // echte Server-ID
    updatedAt: number;
};

class OfflineDB extends Dexie {
    entities!: Table<EntityRow, number>;
    outbox!: Table<OutboxRow, string>;
    idmap!: Table<IdMapRow, [string, string, string]>; // [cid+collection+scopeKey]

    constructor() {
        super('ammsas-offline');
        this.version(1).stores({
            entities: '++pk, [collection+scopeKey], [collection+scopeKey+serverId], [collection+scopeKey+clientId], [collection+scopeKey+subsetKey], updatedAt, pending',
            outbox: 'id, method, url, createdAt',
            idmap: '[cid+collection+scopeKey], updatedAt'
        });
    }
}
export const db = new OfflineDB();

// --- Encryption Hooks (stub) ---
function enc(obj: any): string {
    // TODO: libsodium: return seal(JSON.stringify(obj))
    return JSON.stringify(obj ?? null);
}
function dec<T>(txt: string, fallback: T): T {
    try { return JSON.parse(txt) as T; } catch { return fallback; }
}

// --- Entity Repo (web) ---
export const entityRepo = {
    async dropTable() {
        await db.entities.clear();
    },
    async getAll<T>(collection: string, scopeKey: string): Promise<T[]> {
        const rows = await db.entities.where('[collection+scopeKey]').equals([collection, scopeKey]).sortBy('updatedAt');
        return rows.map(r => dec<T>(r.dataEnc, null as any)).filter(Boolean);
    },

    async upsertLocal<T>(collection: string, scopeKey: string, data: T, clientId: string, opts?: { pending?: boolean }): Promise<void> {
        const prev = await db.entities.where('[collection+scopeKey+clientId]').equals([collection, scopeKey, clientId]).first();
        await db.entities.put({
            pk: prev?.pk,
            collection, scopeKey,
            clientId, serverId: prev?.serverId ?? null,
            pending: opts?.pending ?? prev?.pending ?? false,
            subsetKey: prev?.subsetKey ?? null,
            updatedAt: Date.now(),
            dataEnc: enc(data),
        });
    },

    async upsertByServerId<T>(collection: string, scopeKey: string, serverId: string, data: T, opts?: { pending?: boolean, subsetKey?: string | null }): Promise<void> {
        const prev = await db.entities.where('[collection+scopeKey+serverId]').equals([collection, scopeKey, serverId]).first();
        await db.entities.put({
            pk: prev?.pk,
            collection, scopeKey,
            serverId, clientId: prev?.clientId ?? null,
            subsetKey: opts?.subsetKey ?? prev?.subsetKey ?? null,
            pending: opts?.pending ?? false,
            updatedAt: Date.now(),
            dataEnc: enc(data),
        });
    },

    async replaceClientWithServer<T>(collection: string, scopeKey: string, clientId: string, serverId: string, data: T) {
        const tx = db.transaction('rw', db.entities, db.idmap, async () => {
            // 1) idmap schreiben
            await db.idmap.put({ cid: clientId, collection, scopeKey, sid: serverId, updatedAt: Date.now() });
            // 2) client-row löschen
            const clientRow = await db.entities.where('[collection+scopeKey+clientId]').equals([collection, scopeKey, clientId]).first();
            if (clientRow?.pk) await db.entities.delete(clientRow.pk);
            // 3) server-row upsert
            await entityRepo.upsertByServerId(collection, scopeKey, serverId, data, { pending: false, subsetKey: clientRow?.subsetKey ?? null });
        });
        await tx;
    },

    async removeByClientId(collection: string, scopeKey: string, clientId: string) {
        await db.entities.where('[collection+scopeKey+clientId]').equals([collection, scopeKey, clientId]).delete();
    },
    async removeByServerId(collection: string, scopeKey: string, serverId: string) {
        await db.entities.where('[collection+scopeKey+serverId]').equals([collection, scopeKey, serverId]).delete();
    },

    async replaceAllByServerId<T>(collection: string, scopeKey: string, list: T[], serverIdOf?: (e: T) => string | undefined, subsetKey?: string | null) {
        const getId = serverIdOf ?? ((e: any) => e?.id ?? e?._id);
        const now = Date.now();
        await db.transaction('rw', db.entities, async () => {
            const existing = await db.entities.where('[collection+scopeKey]').equals([collection, scopeKey]).toArray();
            const incoming = new Set<string>();
            for (const e of list) { const sid = getId(e); if (sid) incoming.add(String(sid)); }

            const toDelete = existing.filter(r => !!r.serverId && !incoming.has(String(r.serverId)) && (subsetKey ? r.subsetKey === subsetKey : true));
            if (toDelete.length) await db.entities.bulkDelete(toDelete.map(r => r.pk!));

            for (const e of list) {
                const sid = getId(e); if (!sid) continue;
                const prev = await db.entities.where('[collection+scopeKey+serverId]').equals([collection, scopeKey, sid]).first();
                await db.entities.put({
                    pk: prev?.pk,
                    collection, scopeKey,
                    serverId: sid, clientId: prev?.clientId ?? null,
                    subsetKey: subsetKey ?? prev?.subsetKey ?? null,
                    pending: false,
                    updatedAt: now,
                    dataEnc: enc(e),
                });
            }
        });
    },

    /** NEU: merge anhand clientId ODER serverId */
    async mergeByAnyId<T>(
        collection: string,
        scopeKey: string,
        anyId: string,
        merger: (prev: T | null) => T,
        opts?: { pending?: boolean }
    ): Promise<void> {
        const isCid = (s?: string | null): s is string =>
            typeof s === 'string' && s.startsWith('cid:');
        // suchen nach sid oder cid
        const bySid = await db.entities.where('[collection+scopeKey+serverId]')
            .equals([collection, scopeKey, anyId]).first();
        const byCid = await db.entities.where('[collection+scopeKey+clientId]')
            .equals([collection, scopeKey, anyId]).first();

        const prev = bySid ?? byCid ?? null;
        const prevData = prev ? dec<T>(prev.dataEnc, null as any) : null;
        const nextData = merger(prevData);

        //  Row vorbereiten
        const row = {
            pk: prev?.pk,
            collection,
            scopeKey,
            serverId: prev?.serverId ?? (isCid(anyId) ? null : anyId),
            clientId: prev?.clientId ?? (isCid(anyId) ? anyId : null),
            subsetKey: prev?.subsetKey ?? null,
            pending: opts?.pending ?? prev?.pending ?? false,
            updatedAt: Date.now(),
            dataEnc: enc(nextData),
        };

        //  speichern
        await db.entities.put(row as any);
    },
    async findServerIdByClientId(collection: string, scopeKey: string, cid: string): Promise<string | null> {
        const row = await db.idmap.where('[cid+collection+scopeKey]').equals([cid, collection, scopeKey]).first();
        return row?.sid ?? null;
    }

};

// --- Outbox Repo ---
export const outboxRepo = {
    async dropTable() {
        await db.outbox.clear();
    },
    async add(item: {
        id: string; method: OutboxRow['method']; url: string;
        body: any; headers?: Record<string, string>;
        metaLight: { collection: string; scopeKey: string; op: 'UPSERT' | 'DELETE' | 'CUSTOM' | 'PATCH'; extractIdFromUrl?: boolean };
        clientId?: string | null;
    }) {
        const row: OutboxRow = {
            id: item.id,
            method: item.method,
            url: item.url,
            bodyEnc: enc(item.body ?? null),
            headersEnc: enc(item.headers ?? {}),
            metaEnc: enc(item.metaLight),
            scopeKey: item.metaLight.scopeKey,
            clientId: item.clientId ?? null,
            createdAt: Date.now(),
            replayCount: 0,
        };
        await db.outbox.add(row);
    },
    async listAll(): Promise<OutboxRow[]> {
        return await db.outbox.orderBy('createdAt').toArray();
    },
    async remove(id: string) { await db.outbox.delete(id); },
    async bumpReplay(id: string) {
        const r = await db.outbox.get(id); if (!r) return;
        await db.outbox.put({ ...r, replayCount: r.replayCount + 1 });
    },
    async clearAll() { await db.outbox.clear(); }
};

// --- Id-Rewrite Utils ---
export function deepReplaceIds<T>(input: T, replacer: (s: string) => string): T {
    if (input == null) return input;
    if (typeof input === 'string') return replacer(input) as unknown as T;
    if (Array.isArray(input)) return input.map(x => deepReplaceIds(x, replacer)) as unknown as T;
    if (typeof input === 'object') {
        const out: any = {};
        for (const [k, v] of Object.entries(input)) out[k] = deepReplaceIds(v as any, replacer);
        return out;
    }
    return input;
}
